// Service page content for Change of Course or Institution.
// Sourced from docs/research/australian-visa-pathways.md §5.
// COMPLIANCE: release letters and enrolment are our lane; we coordinate them. A
// registered migration agent advises on every visa-impact judgement and lodges
// any new visa. No guarantees; honest about visa risk when changing.

const changeOfCourse = {
  groupSlug: "student-graduate",
  slug: "change-of-course",
  title: "Change of Course or Institution",
  eyebrow: "Student & Graduate",
  intro:
    "Sometimes the course or provider you started with is not the right fit. You can change while on a student visa, but the rules around release letters, course level, and your visa conditions matter. We coordinate the release and new enrolment cleanly, and a registered migration agent advises on the visa impact so nothing puts your status at risk.",

  chips: [
    { label: "What it is", value: "Switching course or provider" },
    { label: "May need", value: "A release from your provider" },
    { label: "Watch", value: "Course level and visa conditions" },
    { label: "Risk", value: "Getting it wrong can affect your visa" },
  ],

  whoFor: [
    "Students who want to change provider while on a student visa.",
    "Students switching to a different course or qualification level.",
    "People worried a change could jeopardise their visa.",
  ],

  essentials: [
    {
      title: "Release letter rules",
      detail:
        "Switching provider within an early part of your course often requires a release from your current provider. We check whether you need one.",
    },
    {
      title: "Course level matters",
      detail:
        "Moving to a lower level than your visa was granted for can raise questions and may require a new visa.",
    },
    {
      title: "A new Confirmation of Enrolment",
      detail:
        "You need a Confirmation of Enrolment from the new course or provider.",
    },
    {
      title: "Visa conditions still apply",
      detail:
        "You must remain enrolled and maintain satisfactory progress and attendance throughout.",
    },
    {
      title: "Sometimes a new visa is needed",
      detail:
        "Depending on the change, you may need to lodge a new student visa. A registered migration agent tells you when that applies.",
    },
    {
      title: "Genuine study progression",
      detail:
        "Your change should make sense as a genuine study plan, which supports any future application.",
    },
  ],

  process: [
    {
      title: "Review your situation",
      detail:
        "We check where you are in your course, your provider's rules, and your visa conditions.",
    },
    {
      title: "Check release and level",
      detail:
        "We check whether you need a release letter, and a registered migration agent advises whether the new course level affects your visa.",
    },
    {
      title: "Coordinate the new enrolment",
      detail:
        "We work with our admissions support to line up your new Confirmation of Enrolment.",
    },
    {
      title: "Lodge a new visa if required",
      detail:
        "If the change needs a new student visa, we prepare and organise the file, and a registered migration agent lodges it.",
    },
    {
      title: "Keep it moving",
      detail:
        "We help you keep your enrolment current and your documentation organised throughout the switch.",
    },
  ],

  faqs: [
    {
      q: "Can I change my course on a student visa?",
      a: "Often yes, but it depends on how far into your course you are, your provider's release rules, and whether the new course is a different level. A registered migration agent advises on the visa impact, and we coordinate the release and enrolment so the change goes smoothly.",
    },
    {
      q: "Do I need a release letter?",
      a: "Sometimes. If you want to switch provider within an early part of your principal course, you usually need a release from your current provider. We check the rules that apply to you.",
    },
    {
      q: "Will I need a new visa?",
      a: "It depends on the change. Moving to a different qualification level, in particular, can require a new student visa. A registered migration agent confirms whether your change triggers a new application.",
    },
    {
      q: "Could changing course hurt a future application?",
      a: "Unmanaged changes can raise genuine-study questions. A registered migration agent advises on how a change bears on a future application, and we help you gather and organise a consistent, genuine study progression.",
    },
  ],

  related: ["student-500", "student-visa-extension", "graduate-485"],

  lastVerified: "13 July 2026",
};

export default changeOfCourse;
